
import { useContext, useEffect, useState } from 'react';
import {
    Form,
    Row,
    Col,
    Card,
    Badge,
    InputGroup
} from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Buyer, Client, CustomField, Seller } from '../../../../types';
import CustomFields from '../../CustomFields';
import StateContext from '../../../../context/State';
import PortalDataService from '../../../../services/portal.service';
import { FileModel } from '../../../../types';

export type ClientDetailsProps = {
    onBuyersChange: (arg0: {
        buyerName: string;
        birthDate: Date;
        genderCorp: string;
        cellPhoneNumber: string;
        email: string;
        address: string;
        occupation: string;
        employer: string;
        businessAddress: string;
        firstName: string;
        middleName: string;
        lastName: string;
    }[]) => void;
    onSellersChange: (arg0:
        {
            sellerName: string;
            birthDate: any;
            genderCorp: string;
            cellPhoneNumber: string;
            email: string;
            address: string;
            occupation: string;
            employer: string;
            businessAddress: string;
        }[]) => void;
    onClientsChange: (arg0: {
        clientName: string;
        birthDate: Date;
        genderCorp: string;
        cellPhoneNumber: string;
        email: string;
        address: string;
        occupation: string;
        employer: string;
        businessAddress: string;
    }[]) => void;
    customFields: CustomField[];
    onCustomFieldsChange: (customFields: CustomField[]) => void;
    sellers: Seller[];
    clients: Client[];
    buyers: Buyer[];
    touched: string | any[];
    province: string;
    fileInfo: FileModel;
}

const ClientDetails = (props: ClientDetailsProps) => {
    const [inputFields, setInputFields] = useState([{
        buyerName: '',
        sellerName: '',
        clientName: '',
        birthDate: null,
        genderCorp: '',
        cellPhoneNumber: '',
        email: '',
        address: '',
        occupation: '',
        employer: '',
        businessAddress: '',
        firstName: '',
        middleName: '',
        lastName: '',
        headerName: '',
        isEstate: '',
    }]);

    const [touchedBuyers, setTouchedBuyers] = useState<string[][]>([]);
    const [touchedSellers, setTouchedSellers] = useState<string[][]>([]);
    const [touchedClients, setTouchedClients] = useState<string[][]>([]);
    const { state } = useContext(StateContext);
    const portalService = new PortalDataService(state);
    const [customFieldsLength, setCustomFieldsLength] = useState<any>(0);

    const handleChange = (index, event) => {
        const { name, value } = event.target;
        const list = [...inputFields];
        list[index][name] = value ? value : '';

        if (props.buyers.length !== 0) { props.onBuyersChange(list) }
        if (props.sellers.length !== 0) { props.onSellersChange(list) }
        if (props.clients.length !== 0) { props.onClientsChange(list) }

    }

    const handleDateChange = (index, value) => {
        const name = 'birthDate';
        const list = [...inputFields];
        list[index][name] = value ? value : '';
        props.onBuyersChange(list);
    }

    function getHeaderName(firstName: string, middleName: string, lastName: string): string {
        return (firstName ?? '') + ' ' + (middleName ?? '') + ' ' + (lastName ?? '');
    }

    useEffect(() => {
        const fetchCustomFields = async () => {
            try {
                const customFieldsResponse = await portalService.getClientPortalCustomFields(state.portalId);
                const perClientFields = customFieldsResponse.filter(field => field.PerClient);
                setCustomFieldsLength(perClientFields.length);
            } catch (error) {
                console.error("Error fetching custom fields: ", error);
            }
        };
    
        fetchCustomFields();
    }, []);

    useEffect(() => {
        if (props.buyers.length !== 0) {
            const buyers: any[] = [];
            for (let i = 0; i < props.buyers.length; i++) {
                const buyer: any = props.buyers[i];

                if (buyer === undefined){
                    continue
                }

                let buyerEmpty = true;

                for (const [, value] of Object.entries(buyer)) {
                    if (value) {
                        buyerEmpty = false;
                        break;
                    }
                }

                if (buyerEmpty) { continue; }
                if (buyer.birthDate === '') { buyer.birthDate = null; }
                if (buyer.birthDate && (buyer.birthDate instanceof Date) === false) {
                    buyer.birthDate = buyer.birthDate.slice(0, 10)
                    buyer.birthDate = new Date(buyer.birthDate + ' :10:00:00');
                }

                if (props.province === 'Nova Scotia') {
                    if (buyer.clientName === '') {
                        buyer.clientName = buyer.lastName;
                    }
                }

                if (props.province === "Manitoba" || props.province === 'British Columbia' || props.province === 'Saskatchewan') {
                    const fileInfoBuyer: Buyer = props.fileInfo.buyers[i]
                    if (fileInfoBuyer && fileInfoBuyer.firstName && fileInfoBuyer.middleName && fileInfoBuyer.lastName) {
                        buyer.headerName = getHeaderName(fileInfoBuyer.firstName, fileInfoBuyer.middleName, fileInfoBuyer.lastName)
                    }
                    else {
                        buyer.headerName = ''
                    }
                    const firstName = buyer.firstName;
                    const lastName = buyer.lastName;
                    buyer.buyerName = (firstName && lastName) ? `${lastName}, ${firstName}` :
                        firstName ? firstName :
                        lastName ? lastName : '';
                }

                buyers.push(buyer);
            }

            setInputFields(buyers);
            const touBuye: any[] = [];
            for (let i = 0; i < props.touched.length; i++) {
                const touched = props.touched[i];
                if (touched.includes('-')) {
                    // Buyer field changed
                    const field = touched.split('-');
                    if (!touBuye[field[1]]) {
                        touBuye[field[1]] = [];
                    }
                    touBuye[field[1]].push(field[2])
                }
            }
            setTouchedBuyers(touBuye);
        }
    }, [props.buyers]);

    useEffect(() => {
        if (props.sellers.length !== 0) {
            const sellers: any[] = [];
            for (let i = 0; i < props.sellers.length; i++) {
                const seller: any = props.sellers[i];

                if (seller === undefined){
                    continue
                }

                let sellerEmpty = true;

                for (const [, value] of Object.entries(seller)) {
                    if (value) {
                        sellerEmpty = false;
                        break;
                    }
                }

                if (sellerEmpty) { continue; }
                if (seller.birthDate === '') { seller.birthDate = null; }
                if (seller.birthDate && (seller.birthDate instanceof Date) === false) {
                    seller.birthDate = seller.birthDate.slice(0, 10)
                    seller.birthDate = new Date(seller.birthDate + ' :10:00:00');
                }

                if (props.province === 'Nova Scotia') {
                    if (seller.clientName === '') {
                        seller.clientName = seller.lastName;
                    }
                }

                if (props.province === "Manitoba" || props.province === 'British Columbia' || props.province === 'Saskatchewan') {
                    const fileInfoSeller: any = props.fileInfo.sellers[i]
                    if (fileInfoSeller &&fileInfoSeller.firstName && fileInfoSeller.middleName && fileInfoSeller.lastName) {
                        seller.headerName = getHeaderName(fileInfoSeller.firstName, fileInfoSeller.middleName, fileInfoSeller.lastName)
                    }
                    else {
                        seller.headerName = ''
                    }
                    const firstName = seller.firstName;
                    const lastName = seller.lastName;
                    seller.sellerName = (firstName && lastName) ? `${lastName}, ${firstName}` :
                        firstName ? firstName :
                        lastName ? lastName : '';
                }
                sellers.push(seller);
            }

            setInputFields(sellers);
            const touBuye: any[] = [];
            for (let i = 0; i < props.touched.length; i++) {
                const touched = props.touched[i];
                if (touched.includes('-')) {
                    // Buyer field changed
                    const field = touched.split('-');
                    if (!touBuye[field[1]]) {
                        touBuye[field[1]] = [];
                    }
                    touBuye[field[1]].push(field[2])
                }
            }
            setTouchedSellers(touBuye);
        }
    }, [props.sellers]);

    useEffect(() => {
        if (props.clients.length !== 0) {
            const clients: any[] = [];
            for (let i = 0; i < props.clients.length; i++) {
                const client: any = props.clients[i];

                if (client === undefined){
                    continue
                }

                let clientEmpty = true;

                for (const [, value] of Object.entries(client)) {
                    if (value) {
                        clientEmpty = false;
                        break;
                    }
                }

                if (clientEmpty) { continue; }
                if (client.birthDate === '') { client.birthDate = null; }
                if (client.birthDate && (client.birthDate instanceof Date) === false) {
                    client.birthDate = client.birthDate.slice(0, 10)
                    client.birthDate = new Date(client.birthDate + ' :10:00:00');
                }

                if (props.province === 'Nova Scotia') {
                    if (client.clientName === '') {
                        client.clientName = client.lastName;
                    }
                }

                if (props.province === "Manitoba" || props.province === 'British Columbia' || props.province === 'Saskatchewan') {
                    const fileInfoClient: any = props.fileInfo.clients[i]
                    if (fileInfoClient && fileInfoClient.firstName && fileInfoClient.middleName && fileInfoClient.lastName) {
                        client.headerName = getHeaderName(fileInfoClient.firstName, fileInfoClient.middleName, fileInfoClient.lastName)
                    }
                    else {
                        client.headerName = ''
                    }
                    const firstName = client.firstName;
                    const lastName = client.lastName;
                    client.clientName = (firstName && lastName) ? `${lastName}, ${firstName}` :
                        firstName ? firstName :
                        lastName ? lastName : '';
                }
                clients.push(client);
            }

            setInputFields(clients);
            const touBuye: any[] = [];
            for (let i = 0; i < props.touched.length; i++) {
                const touched = props.touched[i];
                if (touched.includes('-')) {
                    // Buyer field changed
                    const field = touched.split('-');
                    if (!touBuye[field[1]]) {
                        touBuye[field[1]] = [];
                    }
                    touBuye[field[1]].push(field[2])
                }
            }
            setTouchedClients(touBuye);
        }
    }, [props.clients]);


    return (
        <>
            {
                inputFields.map((data, index) => {
                    let {
                        buyerName,
                        sellerName,
                        clientName,
                        birthDate,
                        genderCorp,
                        cellPhoneNumber,
                        email,
                        occupation,
                        employer,
                        businessAddress,
                        firstName,
                        middleName,
                        lastName,
                        headerName,
                        isEstate
                    } = data;

                    let name = '';
                    let altHeaderName = '';
                    if (buyerName) { 
                        name = buyerName; 
                    } else if (sellerName) { 
                        name = sellerName;
                    } else if (clientName) { 
                        name = clientName;
                    } else {
                        name = (firstName && lastName) ? `${lastName}, ${firstName}` :
                        firstName ? firstName :
                        lastName ? lastName : '';
                    }
                    if (headerName && headerName !== '') {
                        altHeaderName = headerName
                    }
                    else {
                        const altHeaderFirstName = ( firstName && firstName !== '' ) ? firstName + ' ' : '';
                        const altHeaderMiddleName = ( middleName && middleName !== '' ) ? middleName + ' ' : '';
                        altHeaderName = altHeaderFirstName + altHeaderMiddleName + lastName;
                    }
                    
                    return (

                        <div key={index}>
                            <Card>
                                <Card.Header>
                                    <Card.Title>
                                        {props.buyers.length !== 0 && <>Buyer: {buyerName !== '' ? buyerName : altHeaderName}</>}
                                        {props.sellers.length !== 0 && <>Seller: {sellerName !== '' ? sellerName : altHeaderName}</>}
                                        {props.clients.length !== 0 && <>Client: {clientName !== '' ? clientName : altHeaderName}</>}
                                    </Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    { props.province === "Manitoba" || props.province === 'British Columbia' || props.province === 'Saskatchewan'?
                                        <>
                                            <Row className="mb-3">
                                                { inputFields[index].genderCorp === 'Corporation' ?
                                                    <>
                                                        <Col className="col-6">
                                                            <Form.Label>Corporation</Form.Label>
                                                            <Form.Control
                                                                name='lastName'
                                                                value={lastName}
                                                                onChange={(event) => handleChange(index, event)}
                                                            />
                                                            {touchedBuyers[index] ? (touchedBuyers[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedSellers[index] ? (touchedSellers[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedClients[index] ? (touchedClients[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                        </Col>

                                                        <Col className="col-6">
                                                            <Form.Label>Gender/Corporation</Form.Label>
                                                            <Form.Select
                                                                className="dropdown standard-form"
                                                                name='genderCorp'
                                                                value={genderCorp}
                                                                onChange={(event) => handleChange(index, event)}
                                                            >
                                                                <option value=''>--SELECT ONE--</option>
                                                                <option value='Male'>Male</option>
                                                                <option value='Female'>Female</option>
                                                                <option value='Corporation'>Corporation</option>
                                                            </Form.Select>
                                                            {touchedBuyers[index] ? (touchedBuyers[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedSellers[index] ? (touchedSellers[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedClients[index] ? (touchedClients[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                        </Col>
                                                    </>
                                                    :
                                                    <>
                                                        { isEstate === '1' ?
                                                            <Col>
                                                                <Form.Label>Name</Form.Label>
                                                                <Form.Control
                                                                    name='lastName'
                                                                    value={lastName}
                                                                    onChange={(event) => handleChange(index, event)}
                                                                />
                                                                {touchedBuyers[index] ? (touchedBuyers[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedSellers[index] ? (touchedSellers[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedClients[index] ? (touchedClients[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            </Col>
                                                            :
                                                            <>
                                                                <Col>
                                                                    <Form.Label>First Name</Form.Label>
                                                                    <Form.Control
                                                                        name='firstName'
                                                                        value={firstName}
                                                                        onChange={(event) => handleChange(index, event)}
                                                                    />
                                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('firstName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                    {touchedSellers[index] ? (touchedSellers[index].includes('firstName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                    {touchedClients[index] ? (touchedClients[index].includes('firstName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                </Col>

                                                                <Col>
                                                                    <Form.Label>Middle Name</Form.Label>
                                                                    <Form.Control
                                                                        name='middleName'
                                                                        value={middleName ?? ''}
                                                                        onChange={(event) => handleChange(index, event)}
                                                                    />
                                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('middleName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                    {touchedSellers[index] ? (touchedSellers[index].includes('middleName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                    {touchedClients[index] ? (touchedClients[index].includes('middleName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                </Col>

                                                                <Col>
                                                                    <Form.Label>Last Name</Form.Label>
                                                                    <Form.Control
                                                                        name='lastName'
                                                                        value={lastName}
                                                                        onChange={(event) => handleChange(index, event)}
                                                                    />
                                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                    {touchedSellers[index] ? (touchedSellers[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                    {touchedClients[index] ? (touchedClients[index].includes('lastName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                </Col>
                                                            </>
                                                        }
                                                    </>
                                                }
                                            </Row>
                                            <Row className="mb-3">
                                                <Col className="col-6">
                                                    <Form.Label>Cell Phone Number</Form.Label>
                                                    <Form.Control
                                                        name='cellPhoneNumber'
                                                        value={cellPhoneNumber}
                                                        onChange={(event) => handleChange(index, event)}
                                                        placeholder="eg. +1 234 567890"
                                                    />
                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('cellPhoneNumber') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedSellers[index] ? (touchedSellers[index].includes('cellPhoneNumber') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedClients[index] ? (touchedClients[index].includes('cellPhoneNumber') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                </Col>
                                                <Col className="col-6">
                                                    <Form.Label>Email</Form.Label>
                                                    <Form.Control
                                                        name='email'
                                                        value={email}
                                                        onChange={(event) => handleChange(index, event)}
                                                        placeholder="eg example@mail.com"
                                                    />
                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('email') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedSellers[index] ? (touchedSellers[index].includes('email') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedClients[index] ? (touchedClients[index].includes('email') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                </Col>
                                            </Row>
                                                
                                            { inputFields[index].genderCorp !== 'Corporation' && 
                                                <>
                                                    <Row className="mb-3">
                                                        <Col className="col-6">
                                                            <Form.Label>Birth Date</Form.Label>
                                                            <InputGroup>
                                                                <InputGroup.Text onClick={(e) => {
                                                                    e.currentTarget.nextElementSibling.getElementsByTagName("input")[0].dispatchEvent(new Event("click", {
                                                                        bubbles: true,
                                                                        cancelable: true,
                                                                    }));
                                                                }} >
                                                                    <i className="fa-solid fa-calendar-days" />
                                                                </InputGroup.Text>
                                                                <ReactDatePicker 
                                                                    dateFormat='MM/dd/yyyy'
                                                                    //showIcon
                                                                    selected={birthDate}
                                                                    onChange={(data) => handleDateChange(index, data)}
                                                                    showYearDropdown
                                                                    maxDate={new Date()}
                                                                />
                                                            </InputGroup>


                                                            {touchedBuyers[index] ? (touchedBuyers[index].includes('birthDate') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedSellers[index] ? (touchedSellers[index].includes('birthDate') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedClients[index] ? (touchedClients[index].includes('birthDate') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                        </Col>
                                                                                                        
                                                        <Col className="col-6">
                                                            <Form.Label>Gender/Corporation</Form.Label>
                                                            <Form.Select
                                                                className="dropdown standard-form"
                                                                name='genderCorp'
                                                                value={genderCorp}
                                                                onChange={(event) => handleChange(index, event)}
                                                            >
                                                                <option value=''>--SELECT ONE--</option>
                                                                <option value='Male'>Male</option>
                                                                <option value='Female'>Female</option>
                                                                <option value='Corporation'>Corporation</option>
                                                            </Form.Select>
                                                            {touchedBuyers[index] ? (touchedBuyers[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedSellers[index] ? (touchedSellers[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedClients[index] ? (touchedClients[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                        </Col>
                                                    </Row>
                                                    { isEstate !== '1' && 
                                                    <>
                                                        <h3>Occupation Details</h3>
                                                        <Row>
                                                            <Col className="col-4">
                                                                <Form.Label>Employer</Form.Label>
                                                                <Form.Control
                                                                    name='employer'
                                                                    value={employer}
                                                                    onChange={(event) => handleChange(index, event)}
                                                                />
                                                                {touchedBuyers[index] ? (touchedBuyers[index].includes('employer') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedSellers[index] ? (touchedSellers[index].includes('employer') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedClients[index] ? (touchedClients[index].includes('employer') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            </Col>
                                                            <Col className="col-4">
                                                                <Form.Label>Occupation</Form.Label>
                                                                <Form.Control
                                                                    name='occupation'
                                                                    value={occupation}
                                                                    onChange={(event) => handleChange(index, event)}
                                                                />
                                                                {touchedBuyers[index] ? (touchedBuyers[index].includes('occupation') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedSellers[index] ? (touchedSellers[index].includes('occupation') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedClients[index] ? (touchedClients[index].includes('occupation') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            </Col>
                                                            <Col className="col-4">
                                                                <Form.Label>Business Address</Form.Label>
                                                                <Form.Control
                                                                    className="standard-form"
                                                                    name='businessAddress'
                                                                    value={businessAddress}
                                                                    onChange={(event) => handleChange(index, event)}
                                                                />
                                                                {touchedBuyers[index] ? (touchedBuyers[index].includes('businessAddress') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedSellers[index] ? (touchedSellers[index].includes('businessAddress') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedClients[index] ? (touchedClients[index].includes('businessAddress') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            </Col>
                                                        </Row>
                                                        <br />
                                                        {customFieldsLength > 0 && (
                                                            <>
                                                                <br />
                                                                <h3>Per Client Custom Fields</h3>
                                                                <Row>
                                                                    <CustomFields 
                                                                        displayMode='simple' 
                                                                        editMode={false}
                                                                        contact={data}
                                                                    />
                                                                </Row>
                                                            </>
                                                        )}

                                                    </>
                                                    }
                                                </>
                                            }
                                        </>
                                        :
                                        <>
                                            <Row className="mb-3">
                                                <Col>
                                                    <Form.Label>Full Name</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>
                                                            <i className="fa-solid fa-user status-secondary-light" />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            name={
                                                                (props.buyers.length !== 0 ? 'buyerName' : '') + 
                                                                (props.sellers.length !== 0 ? 'sellerName' : '') + 
                                                                (props.clients.length !== 0 ? 'clientName' : '')
                                                            }
                                                            value={name !== '' ? name : altHeaderName}
                                                            onChange={(event) => handleChange(index, event)}
                                                        />
                                                    </InputGroup>
                                                    
                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('buyerName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedSellers[index] ? (touchedSellers[index].includes('sellerName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedClients[index] ? (touchedClients[index].includes('clientName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                </Col>

                                                <Col>
                                                    <Form.Label>Gender/Corporation</Form.Label>
                                                    <Form.Select
                                                        className="dropdown standard-form"
                                                        name='genderCorp'
                                                        value={genderCorp}
                                                        onChange={(event) => handleChange(index, event)}
                                                    >
                                                        <option value=''>--SELECT ONE--</option>
                                                        <option value='Male'>Male</option>
                                                        <option value='Female'>Female</option>
                                                        <option value='Corporation'>Corporation</option>
                                                    </Form.Select>
                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedSellers[index] ? (touchedSellers[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedClients[index] ? (touchedClients[index].includes('genderCorp') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                </Col>

                                                <Col>
                                                    <Form.Label>Email</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>
                                                            <i className="fa-solid fa-envelope status-secondary-light" />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            className="standard-form"
                                                            name='email'
                                                            value={email}
                                                            onChange={(event) => handleChange(index, event)}
                                                            placeholder="eg example@mail.com"
                                                        />
                                                    </InputGroup>
                                                    
                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('email') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedSellers[index] ? (touchedSellers[index].includes('email') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedClients[index] ? (touchedClients[index].includes('email') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col className="col-4">
                                                    <Form.Label>Cell Phone Number</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>
                                                            <i className="fa-solid fa-phone status-secondary-light" />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            name='cellPhoneNumber'
                                                            value={cellPhoneNumber}
                                                            onChange={(event) => handleChange(index, event)}
                                                            placeholder="eg. +1 234 567890"
                                                        />
                                                    </InputGroup>
                                                    {touchedBuyers[index] ? (touchedBuyers[index].includes('cellPhoneNumber') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedSellers[index] ? (touchedSellers[index].includes('cellPhoneNumber') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                    {touchedClients[index] ? (touchedClients[index].includes('cellPhoneNumber') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                </Col>
                                                <Col className="col-4">
                                                    {
                                                        inputFields[index].genderCorp === 'Corporation' ?
                                                            ''
                                                            :
                                                            <>
                                                                <Form.Label>Birth Date</Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text onClick={(e) => {
                                                                        e.currentTarget.nextElementSibling.getElementsByTagName("input")[0].dispatchEvent(new Event("click", {
                                                                            bubbles: true,
                                                                            cancelable: true,
                                                                        }));
                                                                    }} >
                                                                        <i className="fa-solid fa-calendar-days"
                                                                        />
                                                                    </InputGroup.Text>
                                                                    <ReactDatePicker
                                                                        dateFormat='MM/dd/yyyy'
                                                                        //showIcon
                                                                        selected={birthDate}
                                                                        onChange={(data) => handleDateChange(index, data)}
                                                                        showYearDropdown
                                                                        maxDate={new Date()}
                                                                    />
                                                                </InputGroup>
                                                                {touchedBuyers[index] ? (touchedBuyers[index].includes('birthDate') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedSellers[index] ? (touchedSellers[index].includes('birthDate') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                                {touchedClients[index] ? (touchedClients[index].includes('birthDate') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            </>
                                                    }
                                                </Col>
                                            </Row>

                                            { inputFields[index].genderCorp !== 'Corporation' && isEstate !== '1' && 
                                                <>
                                                    <h3>Occupation Details</h3>
                                                    <Row>
                                                        <Col className="col-4">
                                                            <Form.Label>Employer</Form.Label>
                                                            <Form.Control
                                                                name='employer'
                                                                placeholder="eg. Greenview Bank"
                                                                value={employer}
                                                                onChange={(event) => handleChange(index, event)}
                                                            />

                                                            {touchedBuyers[index] ? (touchedBuyers[index].includes('employer') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedSellers[index] ? (touchedSellers[index].includes('employer') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedClients[index] ? (touchedClients[index].includes('employer') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                        </Col>
                                                        <Col className="col-4">
                                                            <Form.Label>Occupation</Form.Label>
                                                            <Form.Control
                                                                name='occupation'
                                                                placeholder="eg. Financial Advisor"
                                                                value={occupation}
                                                                onChange={(event) => handleChange(index, event)}
                                                            />
                                                            {touchedBuyers[index] ? (touchedBuyers[index].includes('occupation') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedSellers[index] ? (touchedSellers[index].includes('occupation') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedClients[index] ? (touchedClients[index].includes('occupation') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                        </Col>
                                                        <Col className="col-4">
                                                            <Form.Label>Business Address</Form.Label>
                                                            <Form.Control
                                                                name='businessAddress'
                                                                placeholder="eg. 53 Greenview Ave"
                                                                value={businessAddress}
                                                                onChange={(event) => handleChange(index, event)}
                                                            />
                                                            {touchedBuyers[index] ? (touchedBuyers[index].includes('businessAddress') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedSellers[index] ? (touchedSellers[index].includes('businessAddress') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                            {touchedClients[index] ? (touchedClients[index].includes('businessAddress') ? <Badge bg="info" text="dark" pill>Pending</Badge> : '') : ''}
                                                        </Col>
                                                    </Row>
                                                </>
                                            }
                                            <br />
                                            {customFieldsLength > 0 && (
                                                <>
                                                    <br />
                                                    <h3>Per Client Custom Fields</h3>
                                                    <Row>
                                                        <CustomFields 
                                                            displayMode='simple' 
                                                            editMode={false}
                                                            contact={data}
                                                        />
                                                    </Row>
                                                </>
                                            )}
                                        </>
                                    }
                                </Card.Body>
                            </Card>
                        </div>
                    )
                })
            }
        </>
    )
}

export default ClientDetails;